import { Injectable } from '@angular/core';
import { CardSensitiveDetailsSession } from '@finxone-platform/shared/sys-config-types';
import {
  AirwallexCardIframeKind,
  buildAirwallexCardIframeSrc,
  isAirwallexIframeMessageOrigin,
  parseAirwallexIframeEvent,
} from './airwallex-card-iframe';

type SensitiveDetailsMountPoints = {
  panDomId: string;
  expDomId: string;
  cvvDomId: string;
  copyPanDomId?: string;
  copyExpDomId?: string;
  copyCvvDomId?: string;
  iframeHostId?: string;
};

type MarqetaBootstrapConfig = {
  clientAccessToken: string;
  component: {
    showPan: {
      cardPan: {
        domId: string;
        format?: boolean;
        styles?: Record<string, Record<string, string>>;
      };
      cardExp: {
        domId: string;
        format?: boolean;
        styles?: Record<string, Record<string, string>>;
      };
      cardCvv: {
        domId: string;
        styles?: Record<string, Record<string, string>>;
      };
      copyCardPan?: {
        domId: string;
        mode: 'transparent';
        onCopySuccess?: () => void;
        onCopyFailure?: (error: unknown) => void;
      };
      copyCardExp?: {
        domId: string;
        mode: 'transparent';
        onCopySuccess?: () => void;
        onCopyFailure?: (error: unknown) => void;
      };
      copyCardCvv?: {
        domId: string;
        mode: 'transparent';
        onCopySuccess?: () => void;
        onCopyFailure?: (error: unknown) => void;
      };
    };
  };
  callbackEvents?: {
    onSuccess?: () => void;
    onFailure?: (error: unknown) => void;
  };
};

type WindowWithMarqeta = Window & {
  marqeta?: {
    bootstrap: (config: MarqetaBootstrapConfig) => void;
    destroy?: () => void;
  };
};

type RenderCallbacks = {
  onSuccess?: () => void;
  onFailure?: (error: unknown) => void;
  onCancel?: () => void;
  onCopyPanSuccess?: () => void;
  onCopyPanFailure?: (error: unknown) => void;
  onCopyExpSuccess?: () => void;
  onCopyExpFailure?: (error: unknown) => void;
  onCopyCvvSuccess?: () => void;
  onCopyCvvFailure?: (error: unknown) => void;
};

@Injectable({ providedIn: 'root' })
export class CardSensitiveDetailsRendererService {
  private sdkScriptPromise?: Promise<void>;
  private airwallexMessageHandler?: (event: MessageEvent) => void;

  async renderSensitiveDetails(
    session: CardSensitiveDetailsSession,
    mountPoints: SensitiveDetailsMountPoints,
    callbacks?: RenderCallbacks,
  ): Promise<void> {
    if (session.providerSession?.sdk === 'airwallex') {
      await this.renderAirwallexIframe(session, 'details', mountPoints.iframeHostId ?? mountPoints.panDomId, callbacks);
      return;
    }
    const scriptUrl =
      session.providerSession?.scriptUrl ?? 'https://widgets.marqeta.com/marqetajs/2.0.0/marqeta.min.js';
    await this.ensureSdkLoaded(scriptUrl);
    const marqeta = (window as WindowWithMarqeta).marqeta;
    if (!marqeta?.bootstrap) {
      throw new Error('Marqeta.js is not available for provider SDK mode');
    }
    if (!session.providerSession?.accessToken) {
      throw new Error('Missing Marqeta client access token');
    }

    marqeta.destroy?.();
    marqeta.bootstrap({
      clientAccessToken: session.providerSession.accessToken,
      component: {
        showPan: {
          cardPan: {
            domId: mountPoints.panDomId,
            format: true,
            styles: this.getPanStyles(),
          },
          cardExp: {
            domId: mountPoints.expDomId,
            format: true,
            styles: this.getExpStyles(),
          },
          cardCvv: {
            domId: mountPoints.cvvDomId,
            styles: this.getCvvStyles(),
          },
          ...(mountPoints.copyPanDomId
            ? {
                copyCardPan: {
                  domId: mountPoints.copyPanDomId,
                  mode: 'transparent' as const,
                  onCopySuccess: callbacks?.onCopyPanSuccess,
                  onCopyFailure: callbacks?.onCopyPanFailure,
                },
              }
            : {}),
          ...(mountPoints.copyExpDomId
            ? {
                copyCardExp: {
                  domId: mountPoints.copyExpDomId,
                  mode: 'transparent' as const,
                  onCopySuccess: callbacks?.onCopyExpSuccess,
                  onCopyFailure: callbacks?.onCopyExpFailure,
                },
              }
            : {}),
          ...(mountPoints.copyCvvDomId
            ? {
                copyCardCvv: {
                  domId: mountPoints.copyCvvDomId,
                  mode: 'transparent' as const,
                  onCopySuccess: callbacks?.onCopyCvvSuccess,
                  onCopyFailure: callbacks?.onCopyCvvFailure,
                },
              }
            : {}),
        },
      },
      callbackEvents: {
        onSuccess: callbacks?.onSuccess,
        onFailure: callbacks?.onFailure,
      },
    });
  }

  async renderAirwallexIframe(
    session: CardSensitiveDetailsSession,
    kind: AirwallexCardIframeKind,
    hostId: string,
    callbacks?: RenderCallbacks,
  ): Promise<void> {
    const token = session.providerSession?.accessToken;
    const cardId = String(
      (session.providerData as { cardId?: string } | undefined)?.cardId ??
        (session.providerData as { airwallex?: { cardId?: string } } | undefined)?.airwallex?.cardId ??
        '',
    );
    const iframeOrigin = session.providerSession?.iframeOrigin ?? 'https://airwallex.com';
    if (!token || !cardId) {
      throw new Error('Missing Airwallex PAN token or card id');
    }

    const host = document.getElementById(hostId);
    if (!host) {
      throw new Error(`Airwallex iframe host #${hostId} was not found`);
    }

    this.detachAirwallexListener();
    host.replaceChildren();

    const iframe = document.createElement('iframe');
    iframe.title = kind === 'change-pin' ? 'Change card PIN' : kind === 'pin' ? 'Card PIN' : 'Card details';
    iframe.setAttribute('frameborder', '0');
    iframe.style.width = '100%';
    iframe.style.border = '0';
    iframe.style.minHeight = kind === 'change-pin' ? '280px' : kind === 'pin' ? '48px' : '160px';
    iframe.src = buildAirwallexCardIframeSrc(
      {
        token,
        cardId,
        iframeOrigin,
        rules: this.getAirwallexRules(kind),
      },
      kind,
    );
    host.appendChild(iframe);

    this.airwallexMessageHandler = (event: MessageEvent) => {
      if (!isAirwallexIframeMessageOrigin(event.origin)) {
        return;
      }
      const parsed = parseAirwallexIframeEvent(cardId, kind, event.data);
      if (!parsed) {
        return;
      }
      if (parsed.height) {
        iframe.style.height = `${parsed.height}px`;
      }
      if (parsed.type === 'loaded' || parsed.type === 'success') {
        callbacks?.onSuccess?.();
      }
      if (parsed.type === 'cancel') {
        callbacks?.onCancel?.();
      }
      if (parsed.type === 'error') {
        callbacks?.onFailure?.(parsed.error ?? 'airwallex-iframe-error');
      }
    };
    window.addEventListener('message', this.airwallexMessageHandler);
  }

  async teardown(): Promise<void> {
    this.detachAirwallexListener();
    (window as WindowWithMarqeta).marqeta?.destroy?.();
  }

  private detachAirwallexListener(): void {
    if (this.airwallexMessageHandler) {
      window.removeEventListener('message', this.airwallexMessageHandler);
      this.airwallexMessageHandler = undefined;
    }
  }

  private getAirwallexRules(kind: AirwallexCardIframeKind): Record<string, Record<string, string>> {
    if (kind === 'pin') {
      return {
        '.pin': {
          color: 'var(--color-on-background)',
          fontFamily: 'Arial, sans-serif',
          fontSize: '20px',
          fontWeight: '700',
          letterSpacing: '4px',
          backgroundColor: 'transparent',
        },
      };
    }
    if (kind === 'change-pin') {
      return {
        '.change-pin': {
          fontFamily: 'Arial, sans-serif',
          backgroundColor: 'transparent',
        },
        '.change-pin__button--submit': {
          backgroundColor: '#0060ff',
          color: 'white',
        },
      };
    }
    return {
      '.details': {
        font: '14px Arial, sans-serif',
        backgroundColor: 'transparent',
        margin: '0',
      },
      '.details__label': {
        color: 'var(--color-on-background-muted)',
      },
      '.details__value': {
        color: 'var(--color-on-background)',
        fontWeight: '600',
      },
    };
  }

  private async ensureSdkLoaded(scriptUrl?: string): Promise<void> {
    if (!scriptUrl) {
      return;
    }

    if (this.sdkScriptPromise) {
      return this.sdkScriptPromise;
    }

    this.sdkScriptPromise = new Promise<void>((resolve, reject) => {
      const existingScript = document.querySelector(`script[data-sensitive-details-sdk="${scriptUrl}"]`);
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.dataset['sensitiveDetailsSdk'] = scriptUrl;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load sensitive details SDK: ${scriptUrl}`));
      document.head.appendChild(script);
    });

    return this.sdkScriptPromise;
  }

  private getPanStyles(): Record<string, Record<string, string>> {
    return {
      span: {
        color: 'var(--color-on-background)',
        'font-family': 'Arial, sans-serif',
        'font-size': '16px',
        background: 'transparent',
        'font-weight': '600',
        'letter-spacing': '2px',
      },
      'span:hover': {
        color: 'var(--color-on-background)',
      },
    };
  }

  private getExpStyles(): Record<string, Record<string, string>> {
    return {
      span: {
        color: 'var(--color-on-background)',
        'font-family': 'Arial, sans-serif',
        'font-size': '16px',
        background: 'transparent',
        'font-weight': '600',
        'letter-spacing': '1px',
      },
      'span:hover': {
        color: 'var(--color-on-background)',
      },
    };
  }

  private getCvvStyles(): Record<string, Record<string, string>> {
    return {
      span: {
        color: 'var(--color-on-background)',
        'font-family': 'Arial, sans-serif',
        'font-size': '16px',
        background: 'transparent',
      },
      'span:hover': {
        color: 'var(--color-on-background)',
      },
    };
  }
}
